<template>
  <div class="container">
    <div class="header">Apps for {{ providedData.tenant.CompanyName }}</div>
    <div class="description">List of apps for this company.</div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <ts-search
              data-testid="search"
              placeholder="search for apps or vendors"
              @idle="onSearch"
              @search="onSearch"
            ></ts-search>
          </div>
          <div class="card-body">
            <ts-tabs class="tabs" @tab-click="handleTabClick">
              <ts-tab
                label="Installed"
                :counter="filteredApps.Installed.length"
                selected="selected"
              ></ts-tab>
              <ts-tab
                label="Available"
                :counter="filteredApps.Available.length"
              ></ts-tab>
            </ts-tabs>
            <ts-spinner
              data-message="Searching ..."
              data-size="medium"
              data-visible
              v-if="!isReady"
            ></ts-spinner>
            <template v-else>
              <div class="no-result" v-if="!results.length">
                No results were found.
              </div>
              <template v-else>
                <div
                  v-for="app in resultsPage"
                  :key="app.AppId.concat(app.VendorId)"
                  class="apps-list"
                >
                  <ts-list-item
                    :title="app.Name"
                    :subtitle="app.VendorId"
                    class="available-apps"
                  >
                    <!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
                    <ts-app-icon slot="icon-left" :src="getIcon(app)" alt="" />
                  </ts-list-item>
                  <ts-button
                    v-if="selectedTab !== 'Installed'"
                    type="primary"
                    class="install-app"
                    :disabled="app.Activated"
                    :busy="
                      installingApps.some(
                        (installingApp) =>
                          installingApp === app.AppId.concat(app.VendorId)
                      )
                    "
                    @button-click="installApp(app)"
                  >
                    Install App
                  </ts-button>
                </div>
              </template>
            </template>
            <ts-pager
              v-if="isReady"
              data-testid="pagination"
              :per-page="perPage"
              :total-pages="totalPages"
              :active-page="activePage"
              @page-change="pageChange"
              @per-page-change="perPageChange"
            ></ts-pager>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import '@tradeshift/elements.app-icon';
import '@tradeshift/elements.button';
import '@tradeshift/elements.list-item';
import '@tradeshift/elements.pager';
import '@tradeshift/elements.search';
import '@tradeshift/elements.spinner';
import '@tradeshift/elements.tabs';
import '@tradeshift/elements/src/vars.css';
import { notify } from '../../../utils';

export default {
  inject: ['providedData'],
  data() {
    return {
      selectedTab: 'Installed',
      availableApps: [],
      installedApps: [],
      installingApps: [],
      isLoadingAvailableApps: false,
      isLoadingInstalledApps: false,
      searchValue: '',
      perPage: 10,
      activePage: 1,
    };
  },
  computed: {
    filteredApps() {
      return {
        Installed: this.installedApps.filter(({ Name, AppId }) => {
          return (
            !this.searchValue ||
            AppId?.toLowerCase().includes(this.searchValue) ||
            Name?.toLowerCase().includes(this.searchValue)
          );
        }),
        Available: this.availableApps,
      };
    },
    results() {
      return this.filteredApps[this.selectedTab] || [];
    },
    resultsPage() {
      const rangeStart = (this.activePage - 1) * this.perPage;
      const rangeEnd = rangeStart + this.perPage;
      // filter the results to avoid availableApps sparse array from mapping over undefined
      return this.results.slice(rangeStart, rangeEnd).filter(Boolean);
    },
    isReady() {
      return this.selectedTab === 'Available'
        ? !this.isLoadingAvailableApps
        : !this.isLoadingInstalledApps;
    },
    totalPages() {
      return Math.ceil(this.results.length / this.perPage);
    },
  },
  mounted() {
    this.getInstalledApps();
    this.getAvailableApps();
  },
  methods: {
    handleTabClick(detail) {
      const label = detail?.detail?.tab?.label;

      if (label === this.selectedTab) {
        return;
      }

      this.activePage = 1;
      this.selectedTab = label || this.selectedTab;
      this.getAvailableApps();
    },
    getIcon(app) {
      return `/support/get_app_icon/${this.providedData.tenant.CompanyAccountId}/${app.VendorId}/${app.AppId}`;
    },
    getInstalledApps() {
      this.isLoadingInstalledApps = true;

      return axios
        .get(
          `/support/tenants/${this.providedData.tenant.CompanyAccountId}/apps/installed`
        )
        .then(({ data: { AppActivations: result } }) => {
          this.installedApps = result;
        })
        .catch((error) => notify.failed(error))
        .finally(() => {
          this.isLoadingInstalledApps = false;
        });
    },
    getAvailableApps() {
      this.isLoadingAvailableApps = true;

      return axios
        .get(
          `/support/tenants/${this.providedData.tenant.CompanyAccountId}/apps`,
          {
            params: {
              limit: this.perPage,
              page: this.activePage - 1,
              search: this.searchValue,
            },
          }
        )
        .then(({ data: { Items: result, itemCount, numPages } }) => {
          // create a full array so the paging can be consistent w/ "installedApps" which has all results client-side
          // and insert the results in the right index
          // As an optimization we could keep the other pages around by only resetting if the searchValue has changed
          const availableApps = new Array(itemCount);
          const offset = (this.activePage - 1) * this.perPage;
          availableApps.splice(offset, result.length, ...result);

          this.availableApps = availableApps;
        })
        .catch((error) => notify.failed(error))
        .finally(() => {
          this.isLoadingAvailableApps = false;
        });
    },
    installApp({ VendorId, AppId }) {
      this.installingApps.push(AppId.concat(VendorId));
      return axios
        .put('/support/install_app', {
          vendor_id: VendorId,
          app_id: AppId,
          tenant_id: this.providedData.tenant.CompanyAccountId,
        })
        .then((response) => {
          notify.succeeded('App has been installed successfully.');
        })
        .catch((error) => {
          notify.failed('Could not install the app. ', error.message);
        })
        .finally(() =>
          this.installingApps.splice(
            this.installingApps.indexOf(AppId.concat(VendorId)),
            1
          )
        );
    },
    onSearch(event) {
      this.activePage = 1;
      this.searchValue = event.target.value.toLowerCase();
      this.getAvailableApps();
    },
    pageChange({ detail: { newVal } }) {
      this.activePage = newVal;
      this.getAvailableApps();
    },
    perPageChange({ detail: { per_page: perPage } }) {
      this.activePage = 1;
      this.perPage = perPage;
      this.getAvailableApps();
    },
  },
};
</script>

<style scoped>
.header {
  color: var(--ts-color-black);
  font-size: var(--ts-fontsize-xbig);
  font-style: normal;
  font-weight: var(--ts-fontweight-bold);
  line-height: var(--ts-unit-plus);
  margin-bottom: var(--ts-unit-half);
}

.description {
  font-style: normal;
  font-weight: var(--ts-fontsize);
  line-height: var(--ts-unit);
  color: var(--ts-color-slate-lightest);
  margin-bottom: var(--ts-unit);
}

.card-header {
  background-color: var(--ts-color-gray-lightest);
  border-bottom: 1px solid var(--ts-color-gray-light);
  display: lex;
  padding: var(--ts-unit-half);
}

.card-header ts-search {
  flex: 1 1 0;
}

.card-body {
  display: flex;
  flex-direction: column;
  padding: 0;
}

ts-pager {
  border-top: 1px solid var(--ts-color-gray-light);
}

ts-spinner {
  padding: var(--ts-unit-double);
}
.no-result {
  text-align: center;
  padding: var(--ts-unit-double);
  min-height: var(--ts-unit-triple);
}

.apps-list {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.available-apps {
  flex-grow: 6;
}

.install-app {
  align-self: center;
  padding: 10px;
}
</style>
